import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wrench, History, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { Panel, Tag, KpiCard } from "@/components/kit";
import { ConfirmDialog, HistoryDialog, DIALOG_CLS } from "@/components/Dialogs";
import { usePhotoPicker, PhotoPreview } from "@/components/Proof";
import { useStore } from "@/store/AppStore";
import { teamOf, nameMap } from "@/lib/selectors";
import { MAINT_FREQ, M_STATUS, maintStatus, nextCheck, assigneeOf, companyMaintenance } from "@/lib/maintenance";
import { MAINT_CATEGORIES } from "@/data/mock";
import { fmtDate } from "@/lib/format";

const Sel = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

function ItemForm({ initial, team, onSave }) {
  const [f, setF] = useState({ category: initial?.category || "Equipamentos", name: initial?.name || "", frequency: initial?.frequency || "semanal", assigneeId: initial?.assigneeId || "default" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Descreva o item de manutenção.");
    onSave({ ...f, name: f.name.trim(), assigneeId: f.assigneeId === "default" ? null : f.assigneeId });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="maint-form">
      <Field label="Categoria"><Sel value={f.category} onChange={set("category")} options={MAINT_CATEGORIES.map((c) => [c, c])} testId="maint-category-select" /></Field>
      <Field label="Item"><Input value={f.name} onChange={set("name")} placeholder="Ex.: Ar-condicionado da recepção" data-testid="maint-name-input" /></Field>
      <Field label="Frequência"><Sel value={f.frequency} onChange={set("frequency")} options={Object.entries(MAINT_FREQ).map(([k, [l]]) => [k, l])} testId="maint-frequency-select" /></Field>
      <Field label="Quem verifica"><Sel value={f.assigneeId} onChange={set("assigneeId")} options={[["default", "Responsável pela manutenção"], ...team.map((p) => [p.id, p.name])]} testId="maint-assignee-select" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="maint-save-button">Salvar item</Button>
    </form>
  );
}

function CheckForm({ item, onSave }) {
  const [status, setStatus] = useState("ok");
  const [note, setNote] = useState("");
  const { photo, uploading, pick } = usePhotoPicker(null, item.companyId);
  const save = () => {
    if (status === "problema" && !note.trim()) return toast.error("Descreva o problema encontrado.");
    onSave({ status, note: note.trim(), photo });
  };
  const opt = (v, Icon, label) => (
    <button type="button" onClick={() => setStatus(v)} data-testid={`maint-check-${v}`} className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm font-semibold transition-colors ${status === v ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 text-neutral-600 hover:border-gold"}`}>
      <Icon className="h-4 w-4 text-gold" /> {label}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="maint-check-form">
      <p className="-mt-2 text-sm text-neutral-500">{item.category} · {item.name}</p>
      <div className="grid grid-cols-2 gap-3">{opt("ok", CheckCircle2, "Tudo OK")}{opt("problema", AlertTriangle, "Com problema")}</div>
      <label className="flex h-32 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-gold bg-gold-soft/40" data-testid="maint-photo-label">
        <PhotoPreview photo={photo} uploading={uploading} label="Foto (opcional)" />
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="maint-photo-input" />
      </label>
      <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={status === "problema" ? "O que está com problema?" : "Observação (opcional)"} data-testid="maint-note-input" />
      <Button onClick={save} disabled={uploading} className="w-full font-semibold" data-testid="maint-check-save">Registrar verificação</Button>
    </div>
  );
}

const histText = (h) => (h.status === "ok" ? `Verificado · OK${h.note ? ` · ${h.note}` : ""}` : `Problema: ${h.note}`);

function ItemRow({ m, names, company, onCheck, onHistory, onEdit, onDelete }) {
  const st = M_STATUS[maintStatus(m)];
  const who = assigneeOf(m, company);
  return (
    <li data-testid={`maint-item-${m.id}`} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-soft"><Wrench className="h-4 w-4 text-gold-dark" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{m.name}</p>
        <p className="text-xs text-neutral-500">{m.category} · {MAINT_FREQ[m.frequency][0]} · {who ? names[who] : "Sem responsável"} · {m.lastCheck ? `última ${fmtDate(m.lastCheck.date)}` : "sem registro"} · próxima {fmtDate(nextCheck(m))}</p>
        {m.lastCheck?.status === "problema" && <p className="mt-1 text-xs font-medium text-red-700" data-testid={`maint-problem-${m.id}`}>{m.lastCheck.note}</p>}
        {m.lastCheck?.photo && <a href={m.lastCheck.photo} target="_blank" rel="noreferrer" data-testid={`maint-photo-${m.id}`}><img src={m.lastCheck.photo} alt="Foto da verificação" className="mt-2 h-16 w-16 rounded-lg object-cover ring-1 ring-gold/40" /></a>}
      </div>
      <div className="flex items-center gap-1">
        <Tag tone={st.tone} testId={`maint-status-${m.id}`}>{st.label}</Tag>
        <Button size="sm" variant="outline" className="ml-2" onClick={onCheck} data-testid={`maint-verify-${m.id}`}>{m.lastCheck?.status === "problema" ? "Resolver" : "Verificar"}</Button>
        <button aria-label="Histórico" onClick={onHistory} data-testid={`maint-history-${m.id}`} className="rounded p-2 text-neutral-400 hover:text-gold-dark"><History className="h-4 w-4" /></button>
        {onEdit && <button aria-label="Editar" onClick={onEdit} data-testid={`maint-edit-${m.id}`} className="rounded p-2 text-neutral-400 hover:text-gold-dark"><Pencil className="h-4 w-4" /></button>}
        {onDelete && <button aria-label="Excluir" onClick={onDelete} data-testid={`maint-delete-${m.id}`} className="rounded p-2 text-neutral-400 hover:text-gold-dark"><Trash2 className="h-4 w-4" /></button>}
      </div>
    </li>
  );
}

function useMaintDialogs(actor) {
  const { checkMaintenance } = useStore();
  const [dlg, setDlg] = useState(null);
  const close = (v) => !v && setDlg(null);
  const dialogs = (
    <>
      <Dialog open={dlg?.type === "check"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid="maint-check-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Verificação de manutenção</DialogTitle></DialogHeader>
          {dlg?.type === "check" && <CheckForm item={dlg.m} onSave={(c) => { checkMaintenance(dlg.m.id, { ...c, by: actor }); toast.success(c.status === "ok" ? "Verificação registrada." : "Problema registrado. A empresária será avisada."); setDlg(null); }} />}
        </DialogContent>
      </Dialog>
      <HistoryDialog open={dlg?.type === "hist"} onOpenChange={close} title={dlg?.m?.name} history={(dlg?.m?.history || []).map((h) => ({ ...h, text: histText(h) }))} testId="maint-history-dialog" />
    </>
  );
  return { dlg, setDlg, close, dialogs };
}

export function MaintenanceManager({ companyId, actor }) {
  const { state, saveMaintenance, deleteMaintenance, updateCompany } = useStore();
  const { dlg, setDlg, close, dialogs } = useMaintDialogs(actor);
  const company = state.companies.find((c) => c.id === companyId);
  const team = teamOf(state, companyId);
  const names = nameMap(state);
  const items = companyMaintenance(state, companyId);
  const count = (k) => items.filter((m) => maintStatus(m) === k).length;
  const save = (data) => { saveMaintenance({ ...data, id: dlg.m?.id, companyId, ...(dlg.m ? {} : { lastCheck: null, history: [] }) }); toast.success(dlg.m ? "Item atualizado." : "Item de manutenção criado."); setDlg(null); };
  return (
    <div className="space-y-6" data-testid="maintenance-manager">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="maint-kpi-total" icon={Wrench} label="Itens" value={items.length} />
        <KpiCard testId="maint-kpi-ok" icon={CheckCircle2} label="Em dia" value={count("em_dia")} />
        <KpiCard testId="maint-kpi-late" icon={AlertTriangle} label="A verificar" value={count("atrasada") + count("verificar") + count("nunca")} />
        <KpiCard testId="maint-kpi-problem" icon={AlertTriangle} label="Com problema" value={count("problema")} />
      </div>
      <Panel title="Checklist de manutenção" subtitle="Estrutura, equipamentos e instalações da empresa" testId="maint-panel" action={<Button size="sm" onClick={() => setDlg({ type: "edit" })} data-testid="maint-new-button"><Plus className="mr-1 h-4 w-4" /> Novo item</Button>}>
        <div className="mb-4 flex flex-col gap-2 rounded-lg bg-neutral-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium">Responsável pela manutenção</p>
          <div className="sm:w-64"><Sel value={company.maintenanceResponsibleId || "none"} onChange={(v) => { updateCompany(companyId, { maintenanceResponsibleId: v === "none" ? null : v }); toast.success("Responsável atualizada."); }} options={[["none", "Não definida"], ...team.map((p) => [p.id, p.name])]} testId="maint-responsible-select" /></div>
        </div>
        {!items.length && <p className="text-sm text-neutral-400">Nenhum item cadastrado.</p>}
        {MAINT_CATEGORIES.filter((cat) => items.some((m) => m.category === cat)).map((cat) => (
          <div key={cat} className="mt-4" data-testid={`maint-group-${cat}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">{cat}</p>
            <ul className="divide-y divide-neutral-100">
              {items.filter((m) => m.category === cat).map((m) => (
                <ItemRow key={m.id} m={m} names={names} company={company} onCheck={() => setDlg({ type: "check", m })} onHistory={() => setDlg({ type: "hist", m })} onEdit={() => setDlg({ type: "edit", m })} onDelete={() => setDlg({ type: "del", m })} />
              ))}
            </ul>
          </div>
        ))}
      </Panel>
      <Dialog open={dlg?.type === "edit"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid="maint-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{dlg?.m ? "Editar item" : "Novo item de manutenção"}</DialogTitle></DialogHeader>
          {dlg?.type === "edit" && <ItemForm initial={dlg.m} team={team} onSave={save} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={close} title="Excluir item?" description={`${dlg?.m?.name || ""}. O histórico de verificações será apagado.`} testId="maint-delete-dialog" onConfirm={() => { deleteMaintenance(dlg.m.id); toast.success("Item excluído."); }} />
      {dialogs}
    </div>
  );
}

export function CollabMaintenance({ collab, items }) {
  const { state } = useStore();
  const { setDlg, dialogs } = useMaintDialogs(collab.name);
  const company = state.companies.find((c) => c.id === collab.companyId);
  if (!items.length) return null;
  const isResp = company.maintenanceResponsibleId === collab.id;
  return (
    <Panel title="Manutenção" subtitle={isResp ? "Você é a responsável pela manutenção da empresa" : "Itens de manutenção atribuídos a você"} testId="collab-maintenance">
      <ul className="divide-y divide-neutral-100">
        {items.map((m) => <ItemRow key={m.id} m={m} names={nameMap(state)} company={company} onCheck={() => setDlg({ type: "check", m })} onHistory={() => setDlg({ type: "hist", m })} />)}
      </ul>
      {dialogs}
    </Panel>
  );
}
